import { forceUpdate, h, Host } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
export class Image {
    constructor() {
        this.src = undefined;
        this.alt = undefined;
        this.loading = "eager";
        this.width = undefined;
        this.height = undefined;
        this.srcset = undefined;
        this.sizes = undefined;
        this.crossorigin = undefined;
        this.spinner = false;
        this.error = false;
        this.sourceElements = [];
    }
    el;
    observer;
    /**
     * Emitted when the image is loaded and displayed.
     * @event uxReady
     */
    readyEvent;
    /**
     * Emitted when the image cannot be displayed by the component.
     * @event uxError
     */
    errorEvent;
    i18nSubscriberId;
    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }
    componentWillLoad() {
        this.#updateSourceElements();
        this.setDimensions();
    }
    componentDidLoad() {
        this.observer = new MutationObserver(() => {
            this.#updateSourceElements();
        });
        this.observer.observe(this.el, { childList: true, subtree: true, attributes: true });
    }
    setDimensions() {
        if (this.height === undefined) {
            this.el.style.setProperty("--ux-image-height", "auto");
            this.el.style.setProperty("--ux-image-inner-height", "auto");
        }
        else {
            this.el.style.setProperty("--ux-image-height", `${this.height}px`);
            this.el.style.setProperty("--ux-image-inner-height", "100%");
        }
        this.el.style.setProperty("--ux-image-placeholder-padding-top", `${((this.height || 200) / (this.width || 300)) * 100}%`);
        this.el.style.setProperty("--ux-image-inner-position", this.height && this.width ? "absolute" : "relative");
    }
    disconnectedCallback() {
        this.observer.disconnect();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }
    #updateSourceElements() {
        const sourceElements = Array.from(this.el.querySelectorAll("source"));
        this.sourceElements = sourceElements;
    }
    render() {
        const { error, spinner, src, alt, loading, width, height, srcset, sizes, crossorigin, sourceElements } = this;
        if (error) {
            const { t } = i18nProvider;
            const errorMessage = t("contentLoadingError");
            return (h(Host, { role: "img", "aria-label": errorMessage }, h("div", { class: "placeholder-container" }, h("div", { class: "placeholder-content" }, h("ux-icon", { class: "error-icon", name: "cross" }), h("div", { class: "error-message" }, errorMessage), h("div", { class: "error-alt-text" }, alt)))));
        }
        if (spinner) {
            return (h(Host, { role: "img", "aria-label": alt, "aria-busy": "true" }, h("div", { class: "placeholder-container" }, h("div", { class: "placeholder-content" }, h("ux-loader", { variant: "spinner" })))));
        }
        return (h(Host, { role: "img", "aria-label": alt }, Boolean(this.height && this.width) && h("div", { class: "placeholder-container" }), h("picture", null, sourceElements.map((sourceElement) => {
            return (h("source", { srcSet: sourceElement.srcset || undefined, media: sourceElement.media || undefined, type: sourceElement.type || undefined, width: sourceElement.width || undefined, height: sourceElement.height || undefined }));
        }), h("img", { src: src, alt: alt, srcset: srcset, sizes: sizes, loading: loading, width: width, height: height, crossorigin: crossorigin, onError: (e) => {
                this.errorEvent.emit({ originalError: e });
            }, onLoad: () => {
                this.readyEvent.emit();
            } }))));
    }
    static get is() { return "ux-image"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["image.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["image.css"]
        };
    }
    static get properties() {
        return {
            "src": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "required",
                            "text": undefined
                        }],
                    "text": "The source URL of the image."
                },
                "attribute": "src",
                "reflect": false
            },
            "alt": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "required",
                            "text": undefined
                        }],
                    "text": "The alternative text for the image."
                },
                "attribute": "alt",
                "reflect": false
            },
            "loading": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"lazy\" | \"eager\"",
                    "resolved": "\"eager\" | \"lazy\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "\"eager\""
                        }],
                    "text": "The loading strategy for the image.\nCan be either \"lazy\" or \"eager\"."
                },
                "attribute": "loading",
                "reflect": false,
                "defaultValue": "\"eager\""
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "The width of the image in pixels."
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "The height of the image in pixels."
                },
                "attribute": "height",
                "reflect": false
            },
            "srcset": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "The source set attribute for responsive images."
                },
                "attribute": "srcset",
                "reflect": false
            },
            "sizes": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "The sizes attribute for responsive images."
                },
                "attribute": "sizes",
                "reflect": false
            },
            "crossorigin": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"anonymous\" | \"use-credentials\" | undefined",
                    "resolved": "\"anonymous\" | \"use-credentials\" | undefined",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "The cross-origin attribute for the image.\nCan be \"anonymous\", \"use-credentials\", or undefined."
                },
                "attribute": "crossorigin",
                "reflect": false
            },
            "spinner": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false"
                        }],
                    "text": "Whether to show a spinner while the image is loading."
                },
                "attribute": "spinner",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false"
                        }],
                    "text": "Whether to show an error state without emitting the error event."
                },
                "attribute": "error",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "sourceElements": {}
        };
    }
    static get events() {
        return [{
                "method": "readyEvent",
                "name": "uxReady",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": "uxReady"
                        }],
                    "text": "Emitted when the image is loaded and displayed."
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "errorEvent",
                "name": "uxError",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": "uxError"
                        }],
                    "text": "Emitted when the image cannot be displayed by the component."
                },
                "complexType": {
                    "original": "UxErrorEvent",
                    "resolved": "UxErrorEvent",
                    "references": {
                        "UxErrorEvent": {
                            "location": "import",
                            "path": "../../globals/event-types",
                            "id": "src/globals/event-types.ts::UxErrorEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "height",
                "methodName": "setDimensions"
            }, {
                "propName": "width",
                "methodName": "setDimensions"
            }];
    }
}
//# sourceMappingURL=image.js.map
